// R-131-FIN-DECISION-F — Firestore persistence for pathway comparisons.
// R-131-FIN-DECISION-K — pin/unpin site decision artifact + report deep-links.

import type { Pathway } from '../lib/finDecision/types';
import type {
  PathwayComparisonDocument,
  SavedSitePathwayDocument,
} from '../types/pathwayComparison';
// CDS-1 convention: scrub `undefined` at the write site (Firestore rejects it).
// Pathways carry optional metrics that narrow to undefined (e.g. a pure-cost
// pathway's IRR), which crashed setDoc with "Unsupported field value: undefined".
import { stripUndefined } from '../lib/firestore/stripUndefined';

function comparisonCollectionPath(uid: string, siteId: string): string {
  return `users/${uid}/sites/${siteId}/pathwayComparisons`;
}

function savedPathwayCollectionPath(uid: string, siteId: string): string {
  return `users/${uid}/sites/${siteId}/pathways`;
}

export async function savePathwayComparisonToFirestore(args: {
  uid: string;
  siteId: string;
  pathways: Pathway[];
  name: string;
  createdBy: string;
  comparisonId?: string;
}): Promise<PathwayComparisonDocument> {
  const { db } = await import('../firebase');
  const { collection, doc, setDoc } = await import('firebase/firestore');
  const now = new Date().toISOString();
  const id =
    args.comparisonId ??
    doc(collection(db, comparisonCollectionPath(args.uid, args.siteId))).id;

  const payload: PathwayComparisonDocument = {
    id,
    pathwayIds: args.pathways.map((p) => p.id),
    pathways: args.pathways,
    name: args.name,
    createdAt: now,
    createdBy: args.createdBy,
    updatedAt: now,
  };

  await setDoc(
    doc(db, `${comparisonCollectionPath(args.uid, args.siteId)}/${id}`),
    stripUndefined(payload),
  );
  return payload;
}

export async function loadPathwayComparisonFromFirestore(args: {
  uid: string;
  siteId: string;
  comparisonId: string;
}): Promise<PathwayComparisonDocument | null> {
  const { db } = await import('../firebase');
  const { doc, getDoc } = await import('firebase/firestore');
  const snap = await getDoc(
    doc(db, `${comparisonCollectionPath(args.uid, args.siteId)}/${args.comparisonId}`),
  );
  if (!snap.exists()) return null;
  return snap.data() as PathwayComparisonDocument;
}

export async function listPathwayComparisonsFromFirestore(args: {
  uid: string;
  siteId: string;
  limit?: number;
}): Promise<PathwayComparisonDocument[]> {
  const { db } = await import('../firebase');
  const { collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
  const q = query(
    collection(db, comparisonCollectionPath(args.uid, args.siteId)),
    orderBy('updatedAt', 'desc'),
    limit(args.limit ?? 20),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as PathwayComparisonDocument);
}

export async function saveSitePathwayToFirestore(args: {
  uid: string;
  siteId: string;
  pathway: Pathway;
  label: string;
  createdBy: string;
  pathwayDocId?: string;
}): Promise<SavedSitePathwayDocument> {
  const { db } = await import('../firebase');
  const { collection, doc, setDoc } = await import('firebase/firestore');
  const now = new Date().toISOString();
  const id =
    args.pathwayDocId ??
    doc(collection(db, savedPathwayCollectionPath(args.uid, args.siteId))).id;

  const payload: SavedSitePathwayDocument = {
    id,
    pathway: args.pathway,
    label: args.label,
    createdAt: now,
    createdBy: args.createdBy,
    updatedAt: now,
  };

  await setDoc(
    doc(db, `${savedPathwayCollectionPath(args.uid, args.siteId)}/${id}`),
    stripUndefined(payload),
  );
  return payload;
}

export async function listSavedSitePathwaysFromFirestore(args: {
  uid: string;
  siteId: string;
  limit?: number;
}): Promise<SavedSitePathwayDocument[]> {
  const { db } = await import('../firebase');
  const { collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
  const q = query(
    collection(db, savedPathwayCollectionPath(args.uid, args.siteId)),
    orderBy('updatedAt', 'desc'),
    limit(args.limit ?? 30),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as SavedSitePathwayDocument);
}

export function buildPathwayComparisonViewPath(comparisonId: string): string {
  return `?tab=financial&pathwayCompare=${encodeURIComponent(comparisonId)}`;
}

export function buildPathwayComparisonShareUrl(siteId: string, comparisonId: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set('tab', 'financial');
  url.searchParams.set('pathwayCompare', comparisonId);
  return url.toString();
}

export async function pinPathwayComparisonForSite(args: {
  uid: string;
  siteId: string;
  comparisonId: string;
}): Promise<void> {
  const { db } = await import('../firebase');
  const { doc, updateDoc } = await import('firebase/firestore');
  await updateDoc(doc(db, `users/${args.uid}/sites/${args.siteId}`), {
    pinnedPathwayComparisonId: args.comparisonId,
  });
}

export async function unpinPathwayComparisonForSite(args: {
  uid: string;
  siteId: string;
}): Promise<void> {
  const { db } = await import('../firebase');
  const { doc, updateDoc, deleteField } = await import('firebase/firestore');
  await updateDoc(doc(db, `users/${args.uid}/sites/${args.siteId}`), {
    pinnedPathwayComparisonId: deleteField(),
  });
}
